<template>
  <modal
    name="photo-wall-modal"
    class="photo-wall-modal"
    :width="400"
    :height="'auto'"
    @before-open="beforeOpen"
    @before-close="clearData()"
  >
    <div class="photo-wall-modal__header">
      <h3 v-if="photo.uuid">Update post</h3>
      <h3 v-else>Post new image</h3>
      <i class="fa fa-times" aria-hidden="true" @click="closeModal()"></i>
    </div>
    <div class="photo-wall-modal__image">
      <div
        v-if="photo.imageUrl"
        class="image-container"
        :style="{
          'background-image': 'url(' + photo.imageUrl + ')'
        }"
      >
        <div class="change-image" @click="changeImage()">
          <i class="fa fa-upload" aria-hidden="true"></i>
          Change image
        </div>
      </div>
      <div v-show="!photo.imageUrl && !showWebcam" class="photo-wall-modal__image--upload">
        <div>
          <div class="upload-image" @click="clickInput()">
            <i class="fa fa-file-image-o" aria-hidden="true"></i>
            <span>Upload image</span>
          </div>
          <template v-if="isSystemSupported && !deviceError">
            <span>or</span>
            <div class="take-picture" :class="!isDeviceReady ? 'disabled' : null" @click="openCamera">
              <i class="fa fa-camera" aria-hidden="true"></i>
              <span>Take a picture</span>
            </div>
          </template>
        </div>
        <input ref="fileinput" type="file" accept="image/*" @change="onImageUpload($event)" id="photo-input" />
      </div>
      <photo-wall-snapshot
        v-if="showWebcam"
        ref="snapshotComponent"
        @onShapshot="onSnapshot($event)"
      ></photo-wall-snapshot>
    </div>
    <div class="photo-wall-modal__message">
      <label for="image-message"
        >Type message <small>({{ photo.description.length }}/280)</small></label
      >
      <textarea
        name="message"
        v-model="photo.description"
        id="image-message"
        cols="30"
        rows="4"
        maxlength="280"
        placeholder="(max 280 characters)..."
      ></textarea>
    </div>
    <div class="photo-wall-modal__actions actions">
      <v-popover v-if="photo.uuid" :autoHide="true" offset="5" placement="auto-end">
        <button class="delete post-image">Delete</button>
        <template slot="popover">
          <div class="confirm-popover-text">Are you sure?</div>
          <div class="confirm-popover-buttons">
            <button class="btn btn-default" v-close-popover>Cancel</button>
            <button class="btn btn-popover-submit" v-close-popover @click="deletePhoto(photo)">
              Yes, delete
            </button>
          </div>
        </template>
      </v-popover>
      <button class="cancel post-image" @click="closeModal()">Cancel</button>
      <button class="post post-image" :disabled="isDisabled" @click="postPhoto()">
        {{ photo.uuid ? "Save" : "Post" }}
      </button>
    </div>
  </modal>
</template>

<script>
import ImageUploadMixin from "@/components/DigitalEvent/mixins/ImageUploadMixin";
import PhotoWallSnapshot from "./PhotoWallSnapshot";
import { mapActions, mapGetters, mapState } from "vuex";
import { PHOTO_WALL_EXTENSIONS } from "@/helpers/constants";

export default {
  name: "PhotoWallModal",
  mixins: [ImageUploadMixin],
  components: {
    PhotoWallSnapshot
  },
  data() {
    return {
      photo: {
        imageUrl: null,
        description: "",
        uuid: null,
        images: []
      },
      showWebcam: false
    };
  },
  computed: {
    ...mapState({
      eventUUID: state => state.event.config.uuid,
      isDeviceReady: state => state.calls.devices.isReady,
      deviceError: state => state.calls.devices.deviceError
    }),
    ...mapGetters({
      room: "room/activeRoom",
      videoSources: "calls/devices/videoSources",
      isSystemSupported: "calls/devices/isSystemSupported"
    }),
    isDisabled() {
      return !this.photo.imageUrl;
    }
  },
  methods: {
    ...mapActions({
      createGalleryPost: "photoWall/createGalleryPost",
      deleteGalleryPost: "photoWall/deleteGalleryPost"
    }),
    beforeOpen(event) {
      if (event.params && event.params.photo) {
        this.photo.imageUrl = event.params.photo.images[1].imageUrl;
        this.photo.description = event.params.photo.description;
        this.photo.uuid = event.params.photo.uuid;
        this.photo.images = event.params.photo.images;
      }
      document.body.style.overflow = "hidden"; // prevent scroll if modal is opened
      document.body.style.paddingRight = "12px";

      if (!this.isDeviceReady && !this.deviceError) {
        this.$store.dispatch("calls/devices/askVideoPermissionsAndDevices");
      }
    },
    clickInput() {
      document.querySelector("#photo-input").click();
    },
    changeImage() {
      this.photo.imageUrl = null;
    },
    onImageUpload($event) {
      this.photo.imageUrl = this.uploadImage($event, PHOTO_WALL_EXTENSIONS);
      this.$refs.fileinput.value = "";
    },
    closeModal() {
      this.showWebcam = false;
      this.$modal.hide("photo-wall-modal");
      this.clearData();
    },
    clearData() {
      this.imageData.data = null;
      this.photo = {
        imageUrl: null,
        description: ""
      };
      document.body.style.overflow = "auto"; // remove prevent scroll if modal is closed
      document.body.style.paddingRight = "0";
    },
    postPhoto() {
      if (!this.photo.imageUrl) return;

      this.createGalleryPost({
        imageData: this.imageData,
        postData: {
          room_uuid: this.room.uuid,
          event_uuid: this.eventUUID,
          ...this.photo
        }
      });
      this.closeModal();
      this.$emit("imagePosted");
    },
    async onSnapshot(picture) {
      let data = new FormData();
      const base64Response = await fetch(`${picture}`);
      const blob = await base64Response.blob();
      data.append("file", blob);
      data.append("resize_image_profile", "user_public_profile");
      this.imageData.data = data;
      this.imageData.config.header["Content-Type"] = "image/jpeg";
      this.photo.imageUrl = picture;
      this.showWebcam = false;
    },
    openCamera() {
      if (!this.isDeviceReady) return;
      this.showWebcam = !this.showWebcam;
    },
    async deletePhoto(photo) {
      await this.deleteGalleryPost(photo);
      this.closeModal();
    }
  }
};
</script>

<style>
.take-picture.disabled {
  color: #ccc !important;
}
</style>
